/**
 * Browse a project.
 *
 * A tree on the left, the selected file on the right. The split is fixed
 * rather than draggable on purpose: one less piece of state to persist, and
 * the two useful widths (tree legible, preview large) are both achievable by
 * closing the panel entirely.
 */
import { useCallback, useRef, useState, type DragEvent } from 'react'
import { Filter, X, PanelRightClose, PanelRightOpen, Upload } from 'lucide-react'
import type { ProjectId } from '@lattice/core'
import { useUi } from '@/store/ui'
import { useWorkspace } from '@/store/workspace'
import { FileTree } from '@/components/FileTree'
import { FileDetail } from '@/components/FileDetail'
import { SortMenu } from '@/components/SortMenu'
import { Button, Tooltip, EmptyState, Kbd } from '@/components/ui/primitives'
import { cn } from '@/lib/utils'
import { pluralise } from '@/lib/format'

export function FilesView({ projectId, onRename, onDelete, onDownload, onUpload }: {
  projectId: ProjectId
  onRename: (path: string, name: string) => void
  onDelete: (paths: string[]) => void
  onDownload: (paths: string[]) => void
  onUpload: () => void
}) {
  const { openPath, filter, setFilter, detailOpen, setDetailOpen, selection, clearSelection } = useUi()
  const projects = useWorkspace(s => s.projects)
  const project = projects.find(p => p.id === projectId)
  const [dragging, setDragging] = useState(false)
  const dragDepth = useRef(0)

  /**
   * Accept a drop of real files.
   * Depth counting is what stops the overlay flickering as the pointer moves
   * between child elements — dragleave fires constantly otherwise.
   */
  const onDragEnter = useCallback((e: DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return
    dragDepth.current += 1
    setDragging(true)
  }, [])
  const onDragLeave = useCallback(() => {
    dragDepth.current = Math.max(0, dragDepth.current - 1)
    if (dragDepth.current === 0) setDragging(false)
  }, [])
  const onDrop = useCallback((e: DragEvent) => {
    e.preventDefault()
    dragDepth.current = 0
    setDragging(false)
    const files = [...e.dataTransfer.files]
    if (files.length === 0) return
    void import('@/lib/transfer').then(m =>
      m.uploadFiles(projectId, files.map(file => ({
        file,
        // A folder drop gives webkitRelativePath; a file drop gives only a name.
        path: (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name,
      }))),
    )
  }, [projectId])

  return (
    <div
      className="relative flex h-full"
      onDragEnter={onDragEnter}
      onDragOver={e => e.preventDefault()}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <section className={cn('flex min-w-0 flex-col border-r border-[var(--border)]', openPath && detailOpen ? 'w-[46%] min-w-[320px]' : 'flex-1')}>
        <div className="flex h-9 shrink-0 items-center gap-1.5 border-b border-[var(--border)] px-2">
          <Filter className="h-3.5 w-3.5 shrink-0 text-[var(--fg-subtle)]" strokeWidth={1.75} />
          <input
            id="tree-filter"
            value={filter}
            onChange={e => setFilter(e.target.value)}
            onKeyDown={e => { if (e.key === 'Escape') { setFilter(''); e.currentTarget.blur() } }}
            placeholder={`Filter ${project?.name ?? 'project'}…`}
            className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-[var(--fg-subtle)]"
          />
          {filter ? (
            <button onClick={() => setFilter('')} className="snap rounded p-0.5 text-[var(--fg-subtle)] hover:bg-[var(--bg-hover)]" aria-label="Clear filter">
              <X className="h-3 w-3" strokeWidth={2} />
            </button>
          ) : (
            <Kbd keys={['/']} />
          )}

          <div className="mx-0.5 h-4 w-px shrink-0 bg-[var(--border)]" />
          <SortMenu projectId={projectId} />

          {openPath && (
            <Tooltip label={detailOpen ? 'Hide details' : 'Show details'}>
              <Button variant="ghost" size="icon" onClick={() => setDetailOpen(!detailOpen)} aria-label="Toggle details">
                {detailOpen ? <PanelRightClose className="h-3.5 w-3.5" strokeWidth={1.75} /> : <PanelRightOpen className="h-3.5 w-3.5" strokeWidth={1.75} />}
              </Button>
            </Tooltip>
          )}
        </div>

        {selection.size > 0 && (
          <div className="flex h-8 shrink-0 items-center gap-2 border-b border-[var(--border)] bg-[var(--bg-subtle)] px-2 text-xs">
            <span className="tabular text-[var(--fg-muted)]">{pluralise(selection.size, 'item')} selected</span>
            <div className="flex-1" />
            <Button size="sm" variant="ghost" onClick={() => onDownload([...selection])}>Download</Button>
            <Button size="sm" variant="ghost" onClick={() => onDelete([...selection])} className="text-[var(--deleted)]">Delete</Button>
            <Button size="sm" variant="ghost" onClick={clearSelection}>Clear</Button>
          </div>
        )}

        <div className="min-h-0 flex-1">
          <FileTree projectId={projectId} onRename={onRename} onDelete={onDelete} onDownload={onDownload} />
        </div>
      </section>

      {openPath && detailOpen && (
        <section className="min-w-0 flex-1">
          <FileDetail projectId={projectId} path={openPath} onRename={onRename} />
        </section>
      )}

      {!openPath && (
        <section className="hidden min-w-0 flex-1 items-center justify-center lg:flex">
          <EmptyState
            title="Select a file"
            hint="Its preview, full history and discussion appear here. Press ⌘K to search every project at once."
          />
        </section>
      )}

      {dragging && (
        <div className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center bg-[var(--bg)]/80 backdrop-blur-[1px] fade-in">
          <div className="flex flex-col items-center gap-2 rounded-[var(--radius-lg)] border-2 border-dashed border-[var(--accent)] px-10 py-8">
            <Upload className="h-6 w-6 text-[var(--accent)]" strokeWidth={1.5} />
            <p className="text-sm font-medium">Drop to add to {project?.name}</p>
            <p className="text-xs text-[var(--fg-subtle)]">Files already in the workspace upload instantly.</p>
          </div>
        </div>
      )}
    </div>
  )
}
